import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputBase,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { TipTapEditor } from '../../../TipTap';

function InlineOptionsEditor({ options, type, onChange, palette, availablePlaceholders }) {
  const handleAdd = () => onChange([...options, { value: '', label: '' }]);

  const handleUpdateLabel = (idx, label) => {
    const updated = [...options];
    updated[idx] = { ...updated[idx], label };
    if (!updated[idx].value) {
      updated[idx].value = label.toLowerCase().replace(/\s+/g, '_');
    }
    onChange(updated);
  };

  const handleRemove = (idx) => onChange(options.filter((_, i) => i !== idx));

  if (type === 'dropdown') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {options.map((opt, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '4px', '&:hover .remove-btn': { opacity: 1 } }}>
            <Typography sx={{ fontSize: '11px', color: palette.textSecondary, width: 18, textAlign: 'right', flexShrink: 0 }}>
              {idx + 1}.
            </Typography>
            <Box sx={{ flex: 1, minWidth: 80, fontSize: '12px', color: palette.textPrimary }} onClick={(e) => e.stopPropagation()}>
              <TipTapEditor
                value={opt.label || ''}
                onChange={(val) => handleUpdateLabel(idx, val)}
                showToolbar={false}
                stripHtml={true}
                availablePlaceholders={availablePlaceholders}
                placeholder={`Option ${idx + 1}`}
                minHeight="auto"
                className="tiptap-inline"
              />
            </Box>
            {options.length > 1 && (
              <IconButton
                className="remove-btn"
                size="small"
                onClick={() => handleRemove(idx)}
                sx={{ p: '2px', color: palette.textSecondary, opacity: 0, transition: 'opacity 0.15s', '&:hover': { color: palette.errorText || '#b91c1c' } }}
              >
                <CloseIcon sx={{ width: 12, height: 12 }} />
              </IconButton>
            )}
          </Box>
        ))}
        <AddOptionButton onAdd={handleAdd} palette={palette} indent="22px" />
      </Box>
    );
  }

  if (type === 'radio') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <RadioGroup>
          {options.map((opt, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', '&:hover .remove-btn': { opacity: 1 } }}>
              <FormControlLabel
                value={opt.value}
                control={<Radio size="small" disabled />}
                label={
                  <Box sx={{ minWidth: 80, fontSize: '12px', color: palette.textPrimary }} onClick={(e) => e.stopPropagation()}>
                    <TipTapEditor
                      value={opt.label || ''}
                      onChange={(val) => handleUpdateLabel(idx, val)}
                      showToolbar={false}
                      stripHtml={true}
                      availablePlaceholders={availablePlaceholders}
                      placeholder={`Option ${idx + 1}`}
                      minHeight="auto"
                      className="tiptap-inline"
                    />
                  </Box>
                }
                sx={{ mr: 0 }}
              />
              {options.length > 1 && (
                <IconButton
                  className="remove-btn"
                  size="small"
                  onClick={() => handleRemove(idx)}
                  sx={{ p: '2px', color: palette.textSecondary, opacity: 0, transition: 'opacity 0.15s', '&:hover': { color: palette.errorText || '#b91c1c' } }}
                >
                  <CloseIcon sx={{ width: 12, height: 12 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </RadioGroup>
        <AddOptionButton onAdd={handleAdd} palette={palette} />
      </Box>
    );
  }

  if (type === 'checkbox') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {options.map((opt, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', '&:hover .remove-btn': { opacity: 1 } }}>
            <FormControlLabel
              control={<Checkbox size="small" disabled />}
              label={
                <Box sx={{ minWidth: 80, fontSize: '12px', color: palette.textPrimary }} onClick={(e) => e.stopPropagation()}>
                  <TipTapEditor
                    value={opt.label || ''}
                    onChange={(val) => handleUpdateLabel(idx, val)}
                    showToolbar={false}
                    stripHtml={true}
                    availablePlaceholders={availablePlaceholders}
                    placeholder={`Option ${idx + 1}`}
                    minHeight="auto"
                    className="tiptap-inline"
                  />
                </Box>
              }
              sx={{ mr: 0 }}
            />
            {options.length > 1 && (
              <IconButton
                className="remove-btn"
                size="small"
                onClick={() => handleRemove(idx)}
                sx={{ p: '2px', color: palette.textSecondary, opacity: 0, transition: 'opacity 0.15s', '&:hover': { color: palette.errorText || '#b91c1c' } }}
              >
                <CloseIcon sx={{ width: 12, height: 12 }} />
              </IconButton>
            )}
          </Box>
        ))}
        <AddOptionButton onAdd={handleAdd} palette={palette} />
      </Box>
    );
  }

  return null; // unsupported selection type
}

function AddOptionButton({ onAdd, palette, indent = '32px' }) {
  return (
    <Box
      onClick={onAdd}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        pt: '2px',
        pl: indent,
        cursor: 'pointer',
        color: palette.textSecondary,
        transition: 'color 0.15s ease',
        alignSelf: 'flex-start',
        '&:hover': { color: palette.textPrimary },
      }}
    >
      <AddIcon sx={{ width: 13, height: 13 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>Add option</Typography>
    </Box>
  );
}

function DropdownPreview({ field, options, onChange, palette, theme, availablePlaceholders }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <TextField
        size="small"
        fullWidth
        select
        value=""
        disabled
      >
        {options.map((option) => (
          <MenuItem key={String(option.value)} value={option.value}>
            {option.label || option.value}
          </MenuItem>
        ))}
      </TextField>

      <InlineOptionsEditor
        options={options}
        type="dropdown"
        onChange={onChange}
        palette={palette}
        availablePlaceholders={availablePlaceholders}
      />
    </Box>
  );
}

export default function SelectionPreview({ field, onUpdate, palette, theme, availablePlaceholders }) {
  const options = field.options || [];
  const handleOptionsChange = (opts) => onUpdate({ options: opts });

  if (field.type === 'dropdown') {
    return (
      <DropdownPreview
        field={field}
        options={options}
        onChange={handleOptionsChange}
        palette={palette}
        theme={theme}
        availablePlaceholders={availablePlaceholders}
      />
    );
  }

  return (
    <InlineOptionsEditor
      options={options}
      type={field.type}
      onChange={handleOptionsChange}
      palette={palette}
      availablePlaceholders={availablePlaceholders}
    />
  );
}
